/**
 * UserStamp — V2 compact user identity component
 *
 * Renders an avatar (with name tooltip) + optional name text + optional timestamp.
 * Uses simple inline flex so it never expands or wraps unexpectedly.
 *
 * Usage:
 *   <UserStamp user={user} />                            // avatar only (name as tooltip)
 *   <UserStamp user={user} showName />                   // avatar + name
 *   <UserStamp user={user} showName timestamp={isoStr} /> // avatar + name + date
 *   <UserStamp user={user} showName label="Verified by" color={COLORS.success.main} timestamp={isoStr} />
 */
import { Avatar, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { User } from '../../../@types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface UserStampProps {
	/** The user to display. Falls back to initials / "?" when photo is missing. */
	user: Partial<User> | null;
	/** Show the user's full name next to the avatar (default: false — avatar-only with tooltip). */
	showName?: boolean;
	/** Optional prefix label, e.g. "Verified by" */
	label?: string;
	/** ISO-8601 timestamp string. Rendered as "date time". */
	timestamp?: string | null;
	/** Accent color applied to the label text and icon (default: inherit). */
	color?: string;
	/** Avatar diameter in px (default: 24). */
	size?: number;
	/** Font size for all text elements (default: '1rem'). */
	fontSize?: string;
	/** Additional sx applied to the root Box. */
	sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getUserName(user: Partial<User> | null): string {
	if (!user) return 'Unknown';
	const first = user.firstName || '';
	const last = user.lastName || '';
	return `${first} ${last}`.trim() || 'Unknown';
}

function getInitials(user: Partial<User> | null): string {
	if (!user) return '?';
	const first = user.firstName?.[0] || '';
	const last = user.lastName?.[0] || '';
	return (first + last).toUpperCase() || '?';
}

function formatTimestamp(iso: string): string {
	const d = new Date(iso);
	const date = d.toLocaleDateString();
	const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	return `${date} ${time}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const UserStamp = ({
	user,
	showName = false,
	label,
	timestamp,
	color,
	size = 28,
	fontSize = '1.15rem',
	sx,
}: UserStampProps) => {
	const name = getUserName(user);

	const avatar = (
		<Avatar
			src={user?.photo || undefined}
			sx={{
				width: size,
				height: size,
				fontSize: `${size * 0.45}px`,
				flexShrink: 0,
			}}>
			{getInitials(user)}
		</Avatar>
	);

	return (
		<Box
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 0.75,
				...((sx as object) || {}),
			}}>
			{/* Optional prefix label */}
			{label && (
				<Typography
					component="span"
					sx={{
						fontSize,
						fontWeight: 600,
						color: color || 'text.primary',
						whiteSpace: 'nowrap',
					}}>
					{label}
				</Typography>
			)}

			{/* Avatar — always show, tooltip with name */}
			<Tooltip title={name} placement="top" arrow>
				{avatar}
			</Tooltip>

			{/* Optional inline name */}
			{showName && (
				<Typography
					component="span"
					sx={{
						fontSize,
						fontWeight: 500,
						whiteSpace: 'nowrap',
					}}>
					{name}
				</Typography>
			)}

			{/* Optional timestamp */}
			{timestamp && (
				<Typography
					component="span"
					sx={{
						fontSize,
						color: 'text.disabled',
						whiteSpace: 'nowrap',
					}}>
					{formatTimestamp(timestamp)}
				</Typography>
			)}
		</Box>
	);
};
